import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { projectsService } from '../../services/api';
import { uploadToCloudinary } from '../../utils/cloudinaryUpload';

import { FaSave, FaTimes } from 'react-icons/fa';
import Loading from '../../components/Common/Loading';

const ProjectForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(!!id);
  const [saving, setSaving] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  
  const [formData, setFormData] = useState({
    title: '',
    category: 'mobility',
    description: '',
    location: '',
    start_date: '',
    end_date: '',
    status: 'upcoming',
    featured: false,
    budget: '',
    raised_amount: '',
    image_url: ''
  });

  useEffect(() => {
    if (id) {
      fetchProject();
    } else {
      setLoading(false);
    }
  }, [id]);

  const fetchProject = async () => {
    try {
      const response = await projectsService.getById(id);
      let project = response.data;
      if (response.data?.success && response.data.data) {
        project = response.data.data;
      }
      setFormData({
        title: project.title || '',
        category: project.category || 'mobility',
        description: project.description || '',
        location: project.location || '',
        start_date: project.start_date || '',
        end_date: project.end_date || '',
        status: project.status || 'upcoming',
        featured: project.featured === true || project.featured === 1 || project.featured === '1' || false,
        budget: project.budget || '',
        raised_amount: project.raised_amount || '',
        image_url: project.image_url || ''
      });
    } catch (error) {
      console.error('Error fetching project:', error);
      navigate('/projects');
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      setUploadingImage(true);
      const result = await uploadToCloudinary(file);
      if (result.success) {
        setFormData(prev => ({ ...prev, image_url: result.data.url }));
      } else {
        alert(result.message || 'Failed to upload image');
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      alert('Error uploading image. Check your Cloudinary configuration.');
    } finally {
      setUploadingImage(false);
    }
  };


  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      if (id) {
        await projectsService.update(id, formData);
      } else {
        await projectsService.create(formData);
      }
      navigate('/projects');
    } catch (error) {
      console.error('Error saving project:', error);
      alert('Error during saving');
    } finally {
      setSaving(false);
    }
  };

  const categories = [
    { value: 'mobility', label: 'International Mobility' },
    { value: 'solidarity', label: 'Solidarity & Development' },
    { value: 'human_rights', label: 'Human Rights' },
    { value: 'education', label: 'Education' },
    { value: 'environment', label: 'Environment' }
  ];

  const statuses = [
    { value: 'ongoing', label: 'Ongoing' },
    { value: 'upcoming', label: 'Upcoming' },
    { value: 'completed', label: 'Completed' }
  ];

  if (loading) return <Loading />;

  return (
    <div className="max-w-4xl mx-auto">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">
          {id ? 'Edit Project' : 'New Project'}
        </h1>
        <p className="mt-1 text-gray-600">Fill in the project information</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Basic Information */}
        <div className="bg-white rounded-xl shadow-md p-6">
          <h2 className="text-lg font-semibold mb-6">General Information</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-gray-700 mb-2">Project Title *</label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({...formData, title: e.target.value})}
                className="w-full px-4 py-3 border rounded-lg focus:outline-none focus:border-purple-500"
                required
              />
            </div>

            <div>
              <label className="block text-gray-700 mb-2">Category *</label>
              <select
                value={formData.category}
                onChange={(e) => setFormData({...formData, category: e.target.value})}
                className="w-full px-4 py-3 border rounded-lg focus:outline-none focus:border-purple-500"
                required
              >
                {categories.map(cat => (
                  <option key={cat.value} value={cat.value}>{cat.label}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-gray-700 mb-2">Location *</label>
              <input
                type="text"
                value={formData.location}
                onChange={(e) => setFormData({...formData, location: e.target.value})}
                className="w-full px-4 py-3 border rounded-lg focus:outline-none focus:border-purple-500"
                required
                placeholder="e.g. Siedlce, Poland"
              />
            </div>

            <div>
              <label className="block text-gray-700 mb-2">Status *</label>
              <select
                value={formData.status}
                onChange={(e) => setFormData({...formData, status: e.target.value})}
                className="w-full px-4 py-3 border rounded-lg focus:outline-none focus:border-purple-500"
                required
              >
                {statuses.map(status => (
                  <option key={status.value} value={status.value}>{status.label}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-gray-700 mb-2">Start Date *</label>
              <input
                type="date"
                value={formData.start_date}
                onChange={(e) => setFormData({...formData, start_date: e.target.value})}
                className="w-full px-4 py-3 border rounded-lg focus:outline-none focus:border-purple-500"
                required
              />
            </div>

            <div>
              <label className="block text-gray-700 mb-2">End Date *</label>
              <input
                type="date"
                value={formData.end_date}
                onChange={(e) => setFormData({...formData, end_date: e.target.value})}
                className="w-full px-4 py-3 border rounded-lg focus:outline-none focus:border-purple-500"
                required
              />
            </div>

            <div>
              <label className="block text-gray-700 mb-2">Budget (€)</label>
              <input
                type="number"
                value={formData.budget}
                onChange={(e) => setFormData({...formData, budget: e.target.value})}
                className="w-full px-4 py-3 border rounded-lg focus:outline-none focus:border-purple-500"
                placeholder="0"
              />
            </div>

            <div>
              <label className="block text-gray-700 mb-2">Raised Amount (€)</label>
              <input
                type="number"
                value={formData.raised_amount}
                onChange={(e) => setFormData({...formData, raised_amount: e.target.value})}
                className="w-full px-4 py-3 border rounded-lg focus:outline-none focus:border-purple-500"
                placeholder="0"
              />
            </div>

            {/* Project Image Upload */}
            <div className="md:col-span-2">
              <label className="block text-gray-700 mb-2">Project Image</label>
              <div className="flex items-center space-x-4">
                {formData.image_url && (
                  <div className="relative w-24 h-24 border rounded-lg overflow-hidden flex-shrink-0 bg-gray-50">
                    <img src={formData.image_url} alt="Preview" className="w-full h-full object-cover" />
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, image_url: '' })}
                      className="absolute top-1 right-1 bg-red-600 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs hover:bg-red-700"
                      title="Remove image"
                    >
                      ✕
                    </button>
                  </div>
                )}
                <div className="flex-1">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="hidden"
                    id="project-image-upload"
                  />
                  <label
                    htmlFor="project-image-upload"
                    className="cursor-pointer inline-flex items-center justify-center px-4 py-2 border border-gray-300 rounded-lg shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none"
                  >
                    {uploadingImage ? 'Uploading...' : 'Choose Image'}
                  </label>
                  <p className="mt-1 text-xs text-gray-500">Allowed formats: JPG, PNG, GIF, WEBP</p>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-6">
            <label className="block text-gray-700 mb-2">Description *</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({...formData, description: e.target.value})}
              rows="6"
              className="w-full px-4 py-3 border rounded-lg focus:outline-none focus:border-purple-500"
              required
              placeholder="Detailed project description..."
            />
          </div>

          <div className="mt-6 flex items-center">
            <input
              type="checkbox"
              id="featured"
              checked={formData.featured}
              onChange={(e) => setFormData({...formData, featured: e.target.checked})}
              className="mr-2"
            />
            <label htmlFor="featured" className="text-gray-700 font-medium">Featured Project</label>
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-4">
          <button
            type="button"
            onClick={() => navigate('/projects')}
            className="px-6 py-3 border rounded-lg hover:bg-gray-50 flex items-center gap-2"
          >
            <FaTimes />
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            className="px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center gap-2"
          >
            <FaSave />
            {saving ? 'Saving...' : (id ? 'Update' : 'Create')}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ProjectForm;